import { autoUpdate, flip, offset, shift, size, useFloating } from '@floating-ui/react';
import clsx from 'clsx';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowLeftIcon, GlobeIcon } from '@shared/assets/icons';
import { i18TranslationNS, Translation } from '@shared/config/i18n';
import { useCloseOnOutsideOrEscape } from '@shared/lib/hooks';

import { useLanguageSwitcher } from '../model';
import { LanguageSwitcherFlyout } from './LanguageSwitcherFlyout';

export const LanguageSwitcherDropdown = memo(() => {
	const { t } = useTranslation(i18TranslationNS);
	const { currentCode, currentLanguage, allLanguages, selectLanguage } = useLanguageSwitcher();

	const [isOpen, setIsOpen] = useState(false);

	const labels = {
		language: t(Translation.LANGUAGESWITCHER_LANGUAGE),
		aria: `${t(Translation.LANGUAGESWITCHER_ARIA_PREFIX)} ${currentLanguage.name}. ${t(Translation.LANGUAGESWITCHER_ARIA_SUFFIX)}.`,
	};

	const { refs, floatingStyles } = useFloating({
		placement: 'left-start',
		strategy: 'fixed',
		open: isOpen,
		middleware: [
			flip({ fallbackPlacements: ['right-start'] }),
			offset(14),
			shift({ padding: 8 }),
			size({
				padding: 8,
				apply({ availableHeight, elements }) {
					elements.floating.style.maxHeight = `${Math.min(availableHeight, 320)}px`;
				},
			}),
		],
		whileElementsMounted: autoUpdate,
	});

	const dismissRefs = useMemo(
		() => [refs.reference, refs.floating],
		[refs.reference, refs.floating],
	);

	const handleClose = useCallback(() => setIsOpen(false), []);

	useCloseOnOutsideOrEscape(dismissRefs, handleClose, isOpen);

	const handleToggle = useCallback(() => setIsOpen((prev) => !prev), []);

	const handleSelect = useCallback(
		(code) => {
			selectLanguage(code);
			setIsOpen(false);
		},
		[selectLanguage],
	);

	return (
		<>
			<button
				ref={refs.setReference}
				type='button'
				className={clsx('nav-item', 'nav-item--dropdown', isOpen && 'nav-item--active')}
				onClick={handleToggle}
				aria-expanded={isOpen}
				aria-haspopup='menu'
				aria-label={labels.aria}
				role='menuitem'
			>
				<div className='nav-item__wrap'>
					<GlobeIcon className='nav-item__icon' aria-hidden='true' focusable='false' />
					<span className='body-medium-base nav-item__title nav-dropdown__language-code'>
						{currentLanguage.label}
					</span>
				</div>

				<ArrowLeftIcon className='nav-item__icon' aria-hidden='true' focusable='false' />
			</button>

			{isOpen && (
				<LanguageSwitcherFlyout
					allLanguages={allLanguages}
					currentCode={currentCode}
					onSelect={handleSelect}
					floatingRef={refs.setFloating}
					floatingStyles={floatingStyles}
					ariaLabel={labels.language}
				/>
			)}
		</>
	);
});

LanguageSwitcherDropdown.displayName = 'LanguageSwitcherDropdown';
